import { useCallback, useEffect, useState } from "react";
import type { ChatMessage } from "../types";

const STORAGE_KEY = "jarvis_history";
const MAX_CONTEXT_MESSAGES = 12; // sent to the backend per request

function loadHistory(): ChatMessage[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveHistory(history: ChatMessage[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(history));
  } catch {
    // Storage may be unavailable (e.g. private mode) — conversation just won't persist.
  }
}

export function clearStoredHistory(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

interface UseConversationResult {
  history: ChatMessage[];
  addMessage: (message: ChatMessage) => void;
  contextForRequest: (upcoming: ChatMessage) => ChatMessage[];
  clearHistory: () => void;
}

export function useConversation(): UseConversationResult {
  const [history, setHistory] = useState<ChatMessage[]>(() => loadHistory());

  useEffect(() => {
    saveHistory(history);
  }, [history]);

  const addMessage = useCallback((message: ChatMessage) => {
    setHistory((current) => [...current, message]);
  }, []);

  const contextForRequest = useCallback(
    (upcoming: ChatMessage) => [...history, upcoming].slice(-MAX_CONTEXT_MESSAGES),
    [history],
  );

  const clearHistory = useCallback(() => {
    setHistory([]);
  }, []);

  return { history, addMessage, contextForRequest, clearHistory };
}
